<template>
  <div class="at">
    <!-- Busca -->
    <div class="at-search-bar">
      <div class="at-search-wrap">
        <svg
          class="at-search-icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="11"
            cy="11"
            r="8"
            stroke="currentColor"
            stroke-width="2"
          />
          <path
            d="M21 21l-4.35-4.35"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
        <input
          v-model="filteredData"
          class="at-search-input"
          type="text"
          placeholder="Buscar pelo nome..."
        />
      </div>
    </div>

    <!-- Modal: prontuário médico -->
    <b-modal id="med-record" ref="med-record" size="xl" hide-footer
hide-header
>
      <MedicalRecord :record-data="recordData" />
    </b-modal>

    <b-modal
      id="pix"
      ref="pix"
      hide-footer
      hide-header
      centered
      dialog-class="pix-modal-dialog"
      body-class="pix-modal-body"
      @hidden="onPixModalClosed"
    >
      <Pix
        :pix-data="currentPixData"
        context="modal"
        @close="$bvModal.hide('pix')"
      />
    </b-modal>

    <!-- Tabela de consultas -->
    <div class="at-table-wrap">
      <b-table
        class="at-table"
        :items="filteredTableData"
        :fields="fields"
        :sort-by="sortBy"
        :busy="busy"
        :filter="filteredData"
        :per-page="perPage"
        @filtered="onFiltered"
      >
        <!-- Coluna: nome (doutor/paciente) -->
        <template #cell(name)="data">
          <div class="at-cell-name">
            <Avatar
              class="at-cell-avatar"
              :src="userPhoto(data.item)"
              :name="getUserName(data.item)"
              size="sm"
            />
            <span class="at-cell-name-text">{{ getUserName(data.item) }}</span>
          </div>
        </template>

        <!-- Coluna: especialidade -->
        <template #cell(specialty)="data">
          {{ data.item.doctor.doctor_property.medical_specialty_name }}
        </template>

        <!-- Coluna: status -->
        <template #cell(status)="data">
          <span
            :id="data.item.id"
            class="badge badge--no-dot"
            :class="{
              'badge--danger': data.item.appointment_cancelation,
              'badge--neutral':
                !data.item.appointment_cancelation &&
                appointmentStatus(data) === 'Realizada',
              'badge--success':
                !data.item.appointment_cancelation &&
                appointmentStatus(data) === 'Pago',
              'badge--warning':
                !data.item.appointment_cancelation &&
                appointmentStatus(data) === 'Pendente',
              'badge--info':
                !data.item.appointment_cancelation &&
                appointmentStatus(data) === 'Agendada'
            }"
          >
            {{ appointmentStatus(data) }}
          </span>

          <b-tooltip
            v-if="data.item.appointment_cancelation"
            :target="data.item.id"
          >
            {{ cancelledAppointmentDate(data.item) }}
          </b-tooltip>
        </template>

        <!-- Coluna: prontuário (médico/recepcionista) -->
        <template #cell(med_record)="data">
          <button class="at-btn at-btn--outline" @click="showModal(data.item)">
            Visualizar
          </button>
        </template>

        <!-- Coluna: ações do paciente -->
        <template #cell(patient_actions)="data">
          <Button
            v-if="canShowPayButton(data.item)"
            class="mr-1"
            size="sm"
            @click.native="handlePayClick(data.item)"
          >
            Pagar
          </Button>
          <button
            class="at-btn at-btn--danger at-btn--sm"
            :disabled="
              data.item.appointment_cancelation || !data.item.can_cancel
            "
            @click="sendCancelAppointment(data.item)"
          >
            Cancelar
          </button>
        </template>

        <!-- Coluna: ações da recepcionista -->
        <template #cell(receptionist_actions)="data">
          <button
            v-if="data.item.room == null"
            class="at-btn at-btn--outline at-btn--sm"
            @click="sendCreateAppointmentRoom(data.item)"
          >
            Criar sala
          </button>
          <button
            v-else
            class="at-btn at-btn--outline at-btn--sm"
            @click="copyAppointmentRoomLink(data.item)"
          >
            Copiar link da sala
          </button>
        </template>

        <!-- Coluna: ações do médico -->
        <template #cell(doctor_actions)="data">
          <template v-if="canManageAppointment(data.item)">
            <button
              v-if="data.item.room == null"
              class="btn btn--outline btn--sm mr-1"
              @click="sendCreateAppointmentRoom(data.item)"
            >
              Criar sala
            </button>
            <button
              v-else
              class="btn btn--success btn--sm mr-1"
              @click="joinRoom(data.item)"
            >
              Entrar na consulta
            </button>
          </template>
          <button
            v-if="canFinishAppointment(data.item)"
            class="at-btn at-btn--brand at-btn--sm mr-1"
            @click="sendFinishAppointment(data.item)"
          >
            Encerrar consulta
          </button>
          <button
            v-if="canManageAppointment(data.item)"
            class="at-btn at-btn--danger at-btn--sm"
            :disabled="
              data.item.appointment_cancelation || !data.item.can_cancel
            "
            @click="sendCancelAppointment(data.item)"
          >
            Cancelar
          </button>
        </template>
      </b-table>

      <!-- Estado: carregando -->
      <div v-if="busy" class="at-state">
        <b-spinner
          style="width: 32px; height: 32px; color: var(--color-primary, #1A6CF7);"
        />
        <span class="at-state-text">Carregando consultas...</span>
      </div>

      <!-- Estado: vazio -->
      <div
        v-if="showEmpty && !filteredTableData.length && !busy"
        class="at-state vm-empty"
      >
        <div class="vm-empty-icon" aria-hidden="true">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
            <rect
              x="3"
              y="4"
              width="18"
              height="18"
              rx="2"
              stroke="currentColor"
              stroke-width="1.5"
            />
            <path
              d="M16 2v4M8 2v4M3 10h18"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
        </div>
        <p class="vm-empty-text">{{ emptyText }}</p>
      </div>

      <Loading
        :active.sync="loading"
        :can-cancel="false"
        :is-full-page="true"
      />
    </div>

    <!-- Rodapé: total e paginação -->
    <div class="at-footer" v-if="tableDataLength">
      <span class="at-footer-count">
        Mostrando
        <b>{{ showingPerPage }}</b>
        de
        <b>{{ tableDataLength }}</b>
        consultas
      </span>

      <button
        v-if="showMoreAppointmentsButton"
        class="at-btn at-btn--link"
        @click="seeMoreAppointments()"
      >
        Ver mais {{ showedPerPageValue }}
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M5 12h14M13 6l6 6-6 6"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <button
        v-else-if="showHideAppointmentsButton"
        class="at-btn at-btn--link"
        @click="hideAllAppointments()"
      >
        Esconder resultado
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M5 12h14M11 6l-6 6 6 6"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import MedicalRecord from "./MedicalRecord";
import Pix from "./Pix";
import Swal from "sweetalert2";
import { showPendingPaymentAlert } from "../utils/pix/pendingPaymentAlert";
import {
  userComputed,
  authComputed,
  userMethods,
  appointmentsMethods
} from "../state/helpers";
import { formatDate } from "./../utils/masks/formatSlotDate";
import { isDoctor, isReceptionist } from "../utils/userRole";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import { parseISO, format, isPast } from "date-fns";
import { pt as locale } from "date-fns/locale";
import Avatar from "./Avatar";

export default {
  name: "AppointmentsTable",
  components: {
    MedicalRecord,
    Loading,
    Pix,
    Avatar
  },
  props: {
    title: { type: String, default: "" },
    apointmentsLength: { type: Number, default: null },
    data: { type: Array, default: () => [] },
    fields: { type: Array, default: () => [] },
    sortBy: { type: String, default: "" },
    showEmpty: { type: Boolean, default: true },
    emptyText: { type: String, default: "" },
    busy: { type: Boolean, default: false }
  },
  data() {
    return {
      currentPixData: {
        qrcode: "",
        qrcode_text: ""
      },
      loading: false,
      filteredData: "",
      filteredDataLength: 0,
      perPage: 5,
      addPerPage: 10,
      recordData: {
        symptoms: [],
        patient: {},
        observation: undefined,
        last_appointment: null,
        appointment_id: ""
      }
    };
  },
  methods: {
    ...userMethods,
    ...appointmentsMethods,

    loadSymptoms(data) {
      this.patient = data.patient;
      let showSymptomsParams = { id: this.patient.id };

      this.$store
        .dispatch("user/showSymptoms", showSymptomsParams)
        .then(response => {
          this.recordData.symptoms = response.data.symptoms;
          this.recordData.patient = {
            ...this.recordData.patient,
            ...response.data.user
          };
          this.loading = false;
        });
    },

    showModal(data) {
      this.loading = true;
      this.recordData.appointment_id = data.id;
      this.recordData.patient = data.patient;
      let showAppointmentParams = { id: data.id };
      this.$store
        .dispatch("appointments/showAppointment", showAppointmentParams)
        .then(response => {
          this.recordData.observation = response.data.observation;
        });
      this.loadSymptoms(data);
      this.$bvModal.show("med-record");
    },

    showPixQRCode(pix) {
      this.currentPixData = pix;
      this.$bvModal.show("pix");
    },

    onPixModalClosed() {
      this.$emit("appointmentPaid");
    },

    canShowPayButton(appointment) {
      const order = appointment.order;

      return !!(
        !appointment.appointment_cancelation &&
        order &&
        order.status === "pending" &&
        order.pix_data
      );
    },

    handlePayClick(appointment) {
      this.loading = true;

      return this.fetchPendingPayment(appointment.id)
        .then(({ data }) => {
          this.showPixQRCode(data.order.pix_data);
        })
        .catch(error => {
          return showPendingPaymentAlert(error).then(result => {
            const errorType =
              error.response &&
              error.response.data &&
              error.response.data.error_type;

            if (errorType === "payment_already_paid" && result.isConfirmed) {
              this.$emit("appointmentPaid");
            }
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    sendCancelAppointment(appointment) {
      Swal.fire({
        title: "Cancelar consulta",
        text: "Você tem certeza que deseja cancelar esta consulta?",
        icon: "question",
        iconHtml: "?",
        iconColor: "#D81A67",
        confirmButtonText: "Continuar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#D81A67",
        showCancelButton: true,
        showCloseButton: true
      }).then(result => {
        if (result.isConfirmed) {
          Swal.fire({
            text:
              "Informe o motivo do cancelamento da consulta (este campo não é obrigatório).",
            input: "textarea",
            confirmButtonText: "Cancelar consulta",
            confirmButtonColor: "#eb5757",
            showLoaderOnConfirm: true,
            preConfirm: appointment_note => {
              const action = isDoctor(this.currentUser)
                ? this.cancelAdminAppointment({
                    appointment_id: appointment.id,
                    note: appointment_note
                  })
                : this.cancelAppointment({
                    appointment_id: appointment.id,
                    note: appointment_note
                  });
              return action
                .then(() => {
                  this.$emit("appointmentCanceled");
                  Swal.fire(
                    "Cancelamento concluido!",
                    "A sua consulta foi cancelada.",
                    "success"
                  );
                })
                .catch(() => {
                  Swal.fire(
                    "Opss!",
                    "Erro no servidor, tente mais tarde.",
                    "error"
                  );
                });
            }
          });
        }
      });
    },
    // Só aparece para o médico, em consulta cuja hora já passou e que ainda não
    // foi encerrada (ended_at nulo). É a rede de segurança para quando o
    // encerramento pela sala de teleconsulta não aconteceu (erro de rede, aba
    // fechada, no-show).
    canFinishAppointment(item) {
      return (
        isDoctor(this.currentUser) &&
        !item.appointment_cancelation &&
        !item.ended_at &&
        isPast(parseISO(item.scheduled_at))
      );
    },
    sendFinishAppointment(appointment) {
      Swal.fire({
        title: "Encerrar consulta?",
        text:
          "Ao confirmar, a consulta será encerrada definitivamente. Nem você nem o paciente poderão reabrir esta sala.",
        icon: "warning",
        confirmButtonText: "Sim, encerrar consulta",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#D81A67",
        showCancelButton: true,
        showCloseButton: true,
        showLoaderOnConfirm: true,
        preConfirm: () => {
          return this.finishAppointment(appointment.id)
            .then(() => {
              this.$emit("appointmentFinished");
              Swal.fire(
                "Consulta encerrada!",
                "A consulta foi encerrada.",
                "success"
              );
            })
            .catch(() => {
              Swal.fire(
                "Opss!",
                "Erro no servidor, tente mais tarde.",
                "error"
              );
            });
        }
      });
    },
    sendCreateAppointmentRoom(appointment) {
      this.$emit("appointmentRoomCreated", appointment);
    },
    copyAppointmentRoomLink(appointment) {
      this.$emit("appointmentRoomLink", appointment);
    },
    joinRoom(appointment) {
      this.$emit("createdRoom", appointment);
    },
    hideModal() {
      this.symptoms = [];
      this.patient = null;
      return this.$bvModal.hide("med-record");
    },
    userPhoto(data) {
      if (isDoctor(this.currentUser) || isReceptionist(this.currentUser))
        return data.patient.photo;
      return data.doctor.photo;
    },
    getUserName(data) {
      if (isDoctor(this.currentUser) || isReceptionist(this.currentUser))
        return data.patient.name;
      return data.doctor.name;
    },
    cancelledAppointmentDate(data) {
      const date = parseISO(data.appointment_cancelation.created_at);
      const formattedDate = format(
        date,
        "'Consulta cancelada dia' dd 'de' MMMM', às ' HH:mm",
        { locale }
      );
      return formattedDate;
    },
    filterTableData() {
      this.filter = this.filteredData;
    },
    appointmentStatus(data) {
      if (data.item.appointment_cancelation) return "Cancelada";
      // "Realizada" = encerrada de verdade pelo médico (ended_at), não pelo
      // relógio. Uma consulta que passou da hora mas não foi encerrada segue
      // como "Pago"/"Agendada" e aparece na aba Próximas com o botão Encerrar.
      if (data.item.ended_at) return "Realizada";

      if (data.item.order && data.item.order.status == "paid") return "Pago";
      if (data.item.order && data.item.order.status == "pending")
        return "Pendente";

      return "Agendada";
    },
    canManageAppointment(item) {
      if (item.appointment_cancelation) return false;
      // Consulta encerrada (ended_at) não tem o que gerenciar — sem sala para
      // reentrar nem o que cancelar — mesmo que o horário marcado ainda esteja
      // no futuro (encerrada antes da hora).
      if (item.ended_at) return false;
      // Sala já criada: o médico pode reentrar a qualquer momento até a
      // consulta ser encerrada, mesmo que o horário agendado já tenha passado.
      if (item.room != null) return true;
      return !isPast(parseISO(item.scheduled_at));
    },
    seeMoreAppointments() {
      if (this.dataLengthDiffer < this.addPerPage) {
        this.perPage += this.dataLengthDiffer;
      } else if (this.perPage < this.tableDataLength) {
        this.perPage += this.addPerPage;
      }
    },
    hideAllAppointments() {
      this.perPage = 5;
      this.addPerPage = 10;
    },
    onFiltered(filteredItems) {
      this.filteredDataLength = filteredItems.length;
      this.perPage = 5;
      this.addPerPage = 10;
    }
  },
  computed: {
    ...authComputed,
    ...userComputed,
    filteredTableData() {
      return this.data.filter(data => {
        const date = formatDate(data.scheduled_at, "dd/M/Y");
        const hour = formatDate(data.scheduled_at, "HH:mm");

        data.formated_scheduled_at = date;
        data.formated_scheduled_at_time = hour;
        return data;
      });
    },
    tableDataLength() {
      return this.filteredData != ""
        ? this.filteredDataLength
        : this.apointmentsLength;
    },
    showingPerPage() {
      return this.tableDataLength > 5 ? this.perPage : this.tableDataLength;
    },
    dataLengthDiffer() {
      return this.tableDataLength - this.perPage;
    },
    showedPerPageValue() {
      if (this.tableDataLength && this.tableDataLength >= 5) {
        return this.dataLengthDiffer < this.addPerPage
          ? this.dataLengthDiffer
          : this.addPerPage;
      }
      return 0;
    },
    showMoreAppointmentsButton() {
      return (
        this.tableDataLength &&
        this.tableDataLength > 5 &&
        this.showedPerPageValue > 0
      );
    },
    showHideAppointmentsButton() {
      return (
        this.tableDataLength &&
        this.tableDataLength > 5 &&
        this.showedPerPageValue === 0
      );
    }
  }
};
</script>

<style lang="scss" scoped>
// Wrapper principal
.at {
  font-family: var(--font-sans, "Nunito Sans", sans-serif);
}

// Barra de busca
.at-search-bar {
  padding: var(--space-5, 20px) var(--space-6, 24px) var(--space-4, 16px);
  border-bottom: 1px solid var(--color-gray-100, #f1f5f9);
}

.at-search-wrap {
  position: relative;
  max-width: 400px;
}

.at-search-icon {
  position: absolute;
  top: 50%;
  left: var(--space-3, 12px);
  transform: translateY(-50%);
  color: var(--color-gray-400, #94a3b8);
  pointer-events: none;
}

.at-search-input {
  width: 100%;
  height: 40px;
  padding: 0 var(--space-4, 16px) 0 var(--space-9, 36px);
  border: 1.5px solid var(--color-gray-200, #e2e8f0);
  border-radius: var(--radius-lg, 12px);
  background: var(--color-bg-page, #f4f6fb);
  font-family: var(--font-sans, "Nunito Sans", sans-serif);
  font-size: var(--font-size-sm, 0.875rem);
  color: var(--color-gray-900, #0f172a);
  outline: none;
  transition: border-color 0.15s;

  &::placeholder {
    color: var(--color-gray-400, #94a3b8);
  }

  &:focus {
    border-color: var(--color-primary, #1a6cf7);
    box-shadow: 0 0 0 3px rgba(26, 108, 247, 0.1);
  }
}

// Área da tabela
.at-table-wrap {
  position: relative;
  min-height: 240px;
  overflow-x: auto;
}

// Tabela DS V2
:deep(.at-table) {
  width: 100%;
  margin: 0;

  thead th {
    font-family: var(--font-sans, "Nunito Sans", sans-serif);
    font-size: var(--font-size-xs, 0.75rem);
    font-weight: var(--fw-bold, 700);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--color-gray-400, #94a3b8);
    border: none;
    padding: var(--space-3, 12px) var(--space-5, 20px);
    background: var(--color-bg-page, #f4f6fb);
    white-space: nowrap;
  }

  tbody tr {
    border-bottom: 1px solid var(--color-gray-100, #f1f5f9);
    transition: background 0.1s;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: var(--color-bg-page, #f4f6fb);
    }
  }

  tbody td {
    font-family: var(--font-sans, "Nunito Sans", sans-serif);
    font-size: var(--font-size-sm, 0.875rem);
    font-weight: var(--fw-semibold, 600);
    color: var(--color-gray-800, #1e293b);
    border: none;
    padding: var(--space-4, 16px) var(--space-5, 20px);
    vertical-align: middle;
    white-space: nowrap;
  }

  // Remove bootstrap table stripes/hover defaults
  &.table-striped tbody tr:nth-of-type(odd) {
    background: transparent;
  }

  &.table-hover tbody tr:hover {
    background: var(--color-bg-page, #f4f6fb);
  }
}

// Célula de nome com avatar
.at-cell-name {
  display: flex;
  align-items: center;
  gap: var(--space-3, 12px);
  min-width: 200px;
}

.at-cell-avatar {
  flex: none;
}

.at-cell-name-text {
  font-weight: var(--fw-bold, 700);
  color: var(--color-gray-900, #0f172a);
}

// Botões de ação
.at-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-1, 4px);
  font-family: var(--font-sans, "Nunito Sans", sans-serif);
  font-size: var(--font-size-sm, 0.875rem);
  font-weight: var(--fw-bold, 700);
  border-radius: var(--radius-md, 8px);
  padding: var(--space-2, 8px) var(--space-4, 16px);
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  border: 1.5px solid transparent;
  white-space: nowrap;

  &--sm {
    font-size: var(--font-size-xs, 0.75rem);
    padding: var(--space-1, 4px) var(--space-3, 12px);
    height: 30px;
  }

  &--primary {
    background: var(--color-primary, #1a6cf7);
    color: #fff;
    border-color: var(--color-primary, #1a6cf7);

    &:hover:not(:disabled) {
      background: var(--color-primary-dark, #1456c6);
      border-color: var(--color-primary-dark, #1456c6);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  &--outline {
    background: #fff;
    color: var(--color-gray-700, #334155);
    border-color: var(--color-gray-300, #cbd5e1);

    &:hover:not(:disabled) {
      border-color: var(--color-gray-500, #64748b);
      color: var(--color-gray-900, #0f172a);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  &--danger {
    background: #fef2f2;
    color: #dc2626;
    border-color: #fecaca;

    &:hover:not(:disabled) {
      background: #fee2e2;
      border-color: #fca5a5;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  &--brand {
    background: #fff;
    color: var(--color-primary, #1a6cf7);
    border-color: var(--color-primary, #1a6cf7);

    &:hover:not(:disabled) {
      background: var(--color-bg-page, #f4f6fb);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  &--link {
    background: none;
    border: none;
    color: var(--color-primary, #1a6cf7);
    padding: 0;

    svg {
      transition: transform 0.15s;
    }

    &:hover {
      color: var(--color-primary-dark, #1456c6);

      svg {
        transform: translateX(3px);
      }
    }
  }
}

// Estados: carregando / vazio
.at-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-10, 40px) var(--space-5, 20px);
  gap: var(--space-3, 12px);
  min-height: 200px;
}

.at-state-text {
  font-size: var(--font-size-sm, 0.875rem);
  color: var(--color-gray-400, #94a3b8);
  font-family: var(--font-sans, "Nunito Sans", sans-serif);
}

// Rodapé da tabela
.at-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4, 16px) var(--space-6, 24px);
  border-top: 1px solid var(--color-gray-100, #f1f5f9);
}

.at-footer-count {
  font-size: var(--font-size-sm, 0.875rem);
  color: var(--color-gray-500, #64748b);

  b {
    color: var(--color-gray-800, #1e293b);
  }
}

// Modal Pix
.at-pix-modal {
  padding: var(--space-6, 24px);
  text-align: center;
  position: relative;
}

.at-pix-close {
  position: absolute;
  top: var(--space-4, 16px);
  right: var(--space-4, 16px);
  background: none;
  border: none;
  color: var(--color-gray-400, #94a3b8);
  cursor: pointer;
  padding: var(--space-1, 4px);
  line-height: 1;
  transition: color 0.15s;

  &:hover {
    color: var(--color-gray-700, #334155);
  }
}

.at-pix-title {
  font-family: var(--font-sans, "Nunito Sans", sans-serif);
  font-size: var(--font-size-lg, 1.125rem);
  font-weight: var(--fw-bold, 700);
  color: var(--color-gray-900, #0f172a);
  margin: 0 0 var(--space-5, 20px);
}
</style>

<style lang="scss">
.pix-modal-dialog {
  max-width: 460px;
}

.pix-modal-body {
  padding: 0;
}
</style>
